import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Banknote,
  Edit3,
  FileText,
  Mail,
  MapPin,
  Package,
  Phone,
  Receipt,
  StickyNote,
  User,
} from 'lucide-react';
import { computeClientStats } from '@erp/domain/pro';
import { useDataService } from '@/lib/service-context';
import { DevisStatusBadge, CommandeStatusBadge } from '@/components/common/StatusBadge';
import { formatDate, formatTND } from '@/lib/format';
import { ClientFormModal } from '@/pages/ClientsPage';
import { EMPTY_CLIENT_STATS } from '@/hooks/use-pro-dashboard';
import type { ProClient, ProCommande, ProDevis } from '@erp/data-access/interfaces';

type ActivityItem =
  | { type: 'devis'; date: string; doc: ProDevis }
  | { type: 'commande'; date: string; doc: ProCommande };

export function ClientProfilePage() {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const ds = useDataService();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);

  const clientQuery = useQuery({
    queryKey: ['pro-clients', id],
    queryFn: () => ds.proClients.getById(id),
    enabled: !!id,
  });
  const devisQuery = useQuery({
    queryKey: ['pro-client-profile', id, 'devis'],
    queryFn: () => ds.proDevis.list(),
    enabled: !!id,
  });
  const commandesQuery = useQuery({
    queryKey: ['pro-client-profile', id, 'commandes'],
    queryFn: () => ds.proCommandes.list(),
    enabled: !!id,
  });

  const client = clientQuery.data ?? null;
  const devis = useMemo(
    () => (devisQuery.data ?? []).filter((d) => d.proClientId === id),
    [devisQuery.data, id],
  );
  const commandes = useMemo(
    () => (commandesQuery.data ?? []).filter((c) => c.proClientId === id),
    [commandesQuery.data, id],
  );
  const activity = useMemo<ActivityItem[]>(() => {
    return [
      ...devis.map((doc) => ({ type: 'devis' as const, date: doc.date, doc })),
      ...commandes.map((doc) => ({ type: 'commande' as const, date: doc.date, doc })),
    ]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 8);
  }, [devis, commandes]);

  const isLoading = clientQuery.isLoading || devisQuery.isLoading || commandesQuery.isLoading;

  if (isLoading) {
    return (
      <div className="pro-card p-8 text-[14px]" style={{ color: 'var(--pro-text-grey)' }}>
        ...
      </div>
    );
  }

  if (!client) {
    return (
      <div className="pro-card p-8 text-center">
        <p style={{ color: 'var(--pro-text-grey)' }}>
          {t('pro.clients.profile_not_found', 'Client not found')}
        </p>
        <Link
          to="/clients"
          className="inline-flex items-center gap-1 mt-3 text-[13px] font-medium"
          style={{ color: 'var(--pro-primary)' }}
        >
          <ArrowLeft className="w-4 h-4" />
          {t('pro.clients.back_to_clients', 'Back to clients')}
        </Link>
      </div>
    );
  }

  const name = getClientName(client);
  const address = [client.adresse, client.ville, client.cp].filter(Boolean).join(', ');
  const lastActivity = activity[0]?.date ?? null;
  const stats = computeClientStats(commandes, devis)[0] ?? EMPTY_CLIENT_STATS;

  return (
    <div className="space-y-4 pro-fade-in">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => navigate('/clients')}
          className="inline-flex items-center gap-2 h-10 px-3 rounded-lg text-[13px] font-medium"
          style={{
            border: '1px solid var(--pro-border)',
            color: 'var(--pro-text-dark)',
            background: 'white',
            fontFamily: 'Figtree',
          }}
        >
          <ArrowLeft className="w-4 h-4" />
          {t('pro.clients.back_to_clients', 'Back to clients')}
        </button>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg text-[13px] font-semibold text-white"
          style={{ background: 'var(--pro-primary)', fontFamily: 'Figtree' }}
        >
          <Edit3 className="w-4 h-4" />
          {t('pro.clients.edit', 'Edit')}
        </button>
      </div>

      <section className="pro-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4 min-w-0">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-[20px] font-semibold shrink-0"
              style={{
                background: 'var(--pro-primary-subtle)',
                color: 'var(--pro-primary)',
                fontFamily: 'Figtree',
              }}
            >
              {initials(client)}
            </div>
            <div className="min-w-0">
              <h2
                className="text-[24px] font-semibold leading-tight truncate"
                style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
              >
                {name}
              </h2>
              <div
                className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-[13px]"
                style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
              >
                <InfoInline icon={Phone} value={client.telephone} empty={t('pro.clients.no_phone', 'Pas de téléphone')} />
                <InfoInline icon={Mail} value={client.email} empty={t('pro.clients.no_email', 'Pas d’email')} />
                <InfoInline icon={MapPin} value={address} empty={t('pro.clients.no_address', 'Pas d’adresse')} />
              </div>
            </div>
          </div>
          <div
            className="text-[12px] text-right"
            style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
          >
            <div>{t('pro.clients.created_at', 'Created')}</div>
            <div className="font-medium" style={{ color: 'var(--pro-text-dark)' }}>
              {formatDate(client.createdAt)}
            </div>
          </div>
        </div>
      </section>

      {/* ⚠ Jamais `client.depense` / `.commission` / `.soldeCumule` : ces colonnes
          existent en base mais aucun trigger ne les alimente — elles valent 0
          pour tout le monde. Les chiffres viennent des documents. */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <MetricCard
          icon={Banknote}
          label={t('pro.clients.total_spend', 'Dépenses totales')}
          value={formatTND(stats.revenueTtc)}
        />
        <MetricCard
          icon={Receipt}
          label={t('pro.clients.total_commission', 'Commission totale')}
          value={formatTND(stats.commission)}
        />
        <MetricCard
          icon={Banknote}
          label={t('pro.clients.collected', 'Encaissé')}
          value={formatTND(stats.collected)}
        />
        <MetricCard icon={Package} label={t('pro.clients.orders', 'Commandes')} value={String(commandes.length)} />
        <MetricCard icon={FileText} label={t('pro.clients.quotes', 'Devis')} value={String(devis.length)} />
      </div>

      <div className="grid xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)] gap-4">
        <section className="pro-card p-5 space-y-4">
          <SectionTitle icon={User} title={t('pro.clients.profile_details', 'Client details')} />
          <div className="grid gap-3">
            <Detail label={t('pro.clients.first_name', 'First name')} value={client.prenom} />
            <Detail label={t('pro.clients.last_name', 'Last name')} value={client.nom} />
            <Detail label={t('pro.clients.phone', 'Phone')} value={client.telephone} />
            <Detail label={t('pro.clients.email', 'Email')} value={client.email} />
            <Detail label={t('pro.clients.address', 'Address')} value={client.adresse} />
            <Detail label={t('pro.clients.city', 'City')} value={client.ville} />
            <Detail label={t('pro.clients.postal_code', 'Postal code')} value={client.cp} />
            <Detail label={t('pro.clients.complement', 'Complement')} value={client.complement} />
            <Detail
              label={t('pro.clients.last_activity', 'Last activity')}
              value={lastActivity ? formatDate(lastActivity) : null}
            />
          </div>
          <div className="pt-2" style={{ borderTop: '1px solid var(--pro-line)' }}>
            <SectionTitle icon={StickyNote} title={t('pro.clients.notes', 'Notes')} compact />
            <p
              className="mt-2 text-[13px] leading-6 whitespace-pre-wrap"
              style={{ color: client.notes ? 'var(--pro-text-dark)' : 'var(--pro-text-grey)' }}
            >
              {client.notes || t('pro.clients.no_notes', 'No notes yet')}
            </p>
          </div>
        </section>

        <section className="pro-card p-5 space-y-4">
          <SectionTitle icon={Receipt} title={t('pro.clients.activity', 'Commercial activity')} />
          {activity.length === 0 ? (
            <div className="py-10 text-center">
              <Receipt className="w-9 h-9 mx-auto mb-2" style={{ color: 'var(--pro-text-grey-light)' }} />
              <p className="text-[13px]" style={{ color: 'var(--pro-text-grey)' }}>
                {t('pro.clients.no_activity', 'No quotes or orders for this client yet')}
              </p>
            </div>
          ) : (
            <>
            {/* Mobile: stacked activity cards */}
            <div className="md:hidden space-y-3">
              {activity.map((item) => (
                <ActivityCard
                  key={`${item.type}-${item.doc.code}`}
                  item={item}
                  amountLabel={t('pro.clients.amount', 'Amount')}
                  commissionLabel={t('pro.clients.commission', 'Commission')}
                />
              ))}
            </div>
            {/* Desktop: full table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--pro-line)' }}>
                    <Th>{t('pro.clients.document', 'Document')}</Th>
                    <Th>{t('pro.clients.date', 'Date')}</Th>
                    <Th>{t('pro.clients.status', 'Status')}</Th>
                    <Th align="right">{t('pro.clients.amount', 'Amount')}</Th>
                    <Th align="right">{t('pro.clients.commission', 'Commission')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {activity.map((item) => (
                    <ActivityRow key={`${item.type}-${item.doc.code}`} item={item} />
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </section>
      </div>

      {editing && (
        <ClientFormModal
          initial={client}
          onClose={() => setEditing(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['pro-clients', id] });
            qc.invalidateQueries({ queryKey: ['pro-client-profile', id] });
          }}
        />
      )}
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Banknote;
  label: string;
  value: string;
}) {
  return (
    <div className="pro-card p-4 flex items-center gap-3">
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: 'var(--pro-primary-subtle)', color: 'var(--pro-primary)' }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[12px]" style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}>
          {label}
        </div>
        <div
          className="text-[18px] font-semibold truncate"
          style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function SectionTitle({
  icon: Icon,
  title,
  compact,
}: {
  icon: typeof User;
  title: string;
  compact?: boolean;
}) {
  return (
    <div className={`flex items-center gap-2 ${compact ? '' : 'pb-1'}`}>
      <Icon className="w-4 h-4" style={{ color: 'var(--pro-primary)' }} />
      <h3
        className="text-[15px] font-semibold"
        style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
      >
        {title}
      </h3>
    </div>
  );
}

function InfoInline({
  icon: Icon,
  value,
  empty,
}: {
  icon: typeof Phone;
  value?: string | null;
  empty: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <Icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{value || empty}</span>
    </span>
  );
}

function Detail({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-3">
      <div className="text-[12px]" style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}>
        {label}
      </div>
      <div
        className="text-[13px] font-medium break-words"
        style={{ color: value ? 'var(--pro-text-dark)' : 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
      >
        {value || '-'}
      </div>
    </div>
  );
}

function ActivityRow({ item }: { item: ActivityItem }) {
  const isDevis = item.type === 'devis';
  const to = isDevis ? `/devis/${encodeURIComponent(item.doc.code)}` : `/ordres/${encodeURIComponent(item.doc.code)}`;
  return (
    <tr style={{ borderBottom: '1px solid var(--pro-line)' }} className="hover:bg-orange-50/40">
      <td className="py-3 px-3">
        <Link
          to={to}
          className="inline-flex items-center gap-2 font-semibold"
          style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
        >
          {isDevis ? <FileText className="w-4 h-4" /> : <Package className="w-4 h-4" />}
          {isDevis ? 'DEV' : 'ORD'} #{item.doc.code}
        </Link>
      </td>
      <td className="py-3 px-3" style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}>
        {formatDate(item.date)}
      </td>
      <td className="py-3 px-3">
        {isDevis ? (
          <DevisStatusBadge status={item.doc.status} />
        ) : (
          <CommandeStatusBadge status={item.doc.status} />
        )}
      </td>
      <td className="py-3 px-3 text-right font-medium" style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}>
        {formatTND(item.doc.pttc)}
      </td>
      <td className="py-3 px-3 text-right font-semibold" style={{ color: 'var(--pro-primary)', fontFamily: 'Figtree' }}>
        {formatTND(item.doc.totalCommission)}
      </td>
    </tr>
  );
}

function ActivityCard({
  item,
  amountLabel,
  commissionLabel,
}: {
  item: ActivityItem;
  amountLabel: string;
  commissionLabel: string;
}) {
  const isDevis = item.type === 'devis';
  const to = isDevis
    ? `/devis/${encodeURIComponent(item.doc.code)}`
    : `/ordres/${encodeURIComponent(item.doc.code)}`;
  return (
    <Link
      to={to}
      className="block rounded-xl p-4 space-y-3 active:bg-orange-50/40"
      style={{ border: '1px solid var(--pro-line)' }}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className="inline-flex items-center gap-2 font-semibold text-[13px] min-w-0"
          style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
        >
          {isDevis ? <FileText className="w-4 h-4 shrink-0" /> : <Package className="w-4 h-4 shrink-0" />}
          <span className="truncate">{isDevis ? 'DEV' : 'ORD'} #{item.doc.code}</span>
        </span>
        {isDevis ? (
          <DevisStatusBadge status={item.doc.status} />
        ) : (
          <CommandeStatusBadge status={item.doc.status} />
        )}
      </div>
      <div className="text-[12px]" style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}>
        {formatDate(item.date)}
      </div>
      <div
        className="flex items-center justify-between gap-2 pt-3"
        style={{ borderTop: '1px dashed var(--pro-line)' }}
      >
        <div>
          <div className="text-[11px]" style={{ color: 'var(--pro-text-grey)' }}>{amountLabel}</div>
          <div className="text-[14px] font-medium" style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}>
            {formatTND(item.doc.pttc)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px]" style={{ color: 'var(--pro-text-grey)' }}>{commissionLabel}</div>
          <div className="text-[14px] font-semibold" style={{ color: 'var(--pro-primary)', fontFamily: 'Figtree' }}>
            {formatTND(item.doc.totalCommission)}
          </div>
        </div>
      </div>
    </Link>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={`py-3 px-3 text-[12px] font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}
      style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
    >
      {children}
    </th>
  );
}

function getClientName(client: ProClient): string {
  return [client.prenom, client.nom].filter(Boolean).join(' ').trim() || client.nom;
}

function initials(client: ProClient): string {
  const name = getClientName(client);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]!.charAt(0)}${parts[1]!.charAt(0)}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}
